/* eslint-disable @typescript-eslint/no-shadow */
import _ from 'underscore';

import {
  DismissMode,
  INotification,
  NotificationLevel,
  TargetComponent,
  ScopeLevel,
} from '../../types/notification.types';
import { IContext } from '../../types/context.types';
import { getAlertFlags, IAlertFlags } from './alert-information';
import { i18nMap, shouldRender, isAvailable, isBillingLinkPermission } from '../../utils';

export class NoEntityNotifications {
  context: IContext;
  i18n: any;
  availableScopes: ScopeLevel[];

  constructor(context: IContext) {
    this.context = context;
    this.i18n = this.context.getNewI18n();
    this.availableScopes = [ScopeLevel.account];
  }

  async getNoEligibleEntityNotifications() {
    if (!this.context.getUserContext().CurrentUser.IsInternal && isBillingLinkPermission(this.context.getSettingsContext())) {
      return [];
    }

    const fields = [
      'HasCampaigns',
      'HasNotPausedCampaigns',
      'HasAdGroups',
      'HasNotPausedAdGroups',
      'HasAds',
      'HasNotPausedAds',
      'HasKeywords',
      'HasNotPausedKeywords',
      'HasActiveNonDefaultCampaigns',
    ];

    if (!isAvailable(this.context.getCurrentScope(), this.availableScopes)) {
      return [];
    }

    if (!shouldRender(ScopeLevel.account, this.context.getChangeScope())) {
      return [];
    }

    const alertFlags: IAlertFlags = await getAlertFlags(fields, this.trimScopeToAccount());
    const hasNotEligibleCampaign = (alertFlags: IAlertFlags) =>
      alertFlags.HasCampaigns && !alertFlags.HasNotPausedCampaigns;
    const hasNotEligibleAdGroup = (alertFlags: IAlertFlags) =>
      alertFlags.HasAdGroups && !alertFlags.HasNotPausedAdGroups;
    const hasNotEligibleAd = (alertFlags: IAlertFlags) => alertFlags.HasAds && !alertFlags.HasNotPausedAds;
    const hasNotEligibleKeyword = (alertFlags: IAlertFlags) =>
      !alertFlags.HasActiveNonDefaultCampaigns && alertFlags.HasKeywords && !alertFlags.HasNotPausedKeywords;

    const scopes = _.compact([
      hasNotEligibleCampaign(alertFlags) ? 'Campaign' : null,
      hasNotEligibleAdGroup(alertFlags) ? 'AdGroup' : null,
      hasNotEligibleAd(alertFlags) ? 'Ad' : null,
      hasNotEligibleKeyword(alertFlags) ? 'Keyword' : null,
    ]);

    return _.compact(scopes.map(scope => this.generateErrorNotification(scope)));
  }

  trimScopeToAccount() {
    const scope = this.context.getCurrentScope();
    const values = _.assign({}, scope.values);
    values.adGroupId = undefined;
    values.campaignId = undefined;

    return {
      values,
      location: scope.location,
    };
  }

  generateErrorNotification(scope: string | null): INotification | null {
    // TODO: add walkme link to error message action when walkme is ready
    return {
      target: TargetComponent.TopBanner,
      dismissMode: DismissMode.never,
      severity: NotificationLevel.Error,
      title: '',
      scopeLevel: ScopeLevel.account,
      availableScopes: this.availableScopes,
      message: this.i18n.getString((i18nMap as any)[`Alert_NoEligible_${scope}`]),
      condition: true,
      id: `NoEligible_${scope}`,
    };
  }
}
